import { useAuth } from '../../contexts/AuthContext';
import { ANALYSIS_OPTIONS } from '../../../shared/types';

/* Onglet « Utilisation » des paramètres — extrait tel quel de
 * AccountSettingsPage.tsx (markup/classes identiques). */

export interface UsageStats {
  monthlyAnalyses: number;
  totalAnalyses: number;
  byType: Record<string, number>;
  freeAnalysesUsed: number;
  freeAnalysesLimit: number;
  creditBalance: number;
  plan: string;
}

export default function UsageTabContent({
  usageStats,
  usageLoading,
  planLabel,
}: {
  usageStats: UsageStats | null;
  usageLoading: boolean;
  planLabel: string;
}) {
  const { features } = useAuth();
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white shader:text-white">Utilisation</h2>

      {usageLoading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Chargement...</div>
      ) : usageStats ? (
        <>
          {/* Monthly gauge */}
          <div className="bg-slate-50 dark:bg-slate-800/50 shader:bg-white/5 rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white shader:text-white">Ce mois-ci</h3>

            {features.unlimited ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400 shader:text-white/85">Analyses effectuées</span>
                  <span className="font-medium text-slate-900 dark:text-white shader:text-white flex items-center gap-1.5">
                    {usageStats.monthlyAnalyses}
                    <span className="text-slate-400 dark:text-slate-500 shader:text-white/70">/</span>
                    <span className="text-emerald-600 dark:text-emerald-400 text-base leading-none">∞</span>
                  </span>
                </div>
                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full w-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500"
                    style={{ boxShadow: '0 0 8px 1px rgba(16, 185, 129, 0.55), 0 0 16px 2px rgba(16, 185, 129, 0.25)' }}
                  />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80">Analyses illimitées avec votre plan {planLabel}</p>
              </div>
            ) : usageStats.freeAnalysesLimit === 0 ? (
              // Modèle 100% payant : le plan `free` ne comprend aucune analyse incluse.
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-900 dark:text-white shader:text-white">Aucune analyse incluse</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80">
                  Votre plan ne comprend aucune analyse incluse. Abonnez-vous (Pro ou Premium) ou achetez un bundle pour lancer des analyses.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400 shader:text-white/85">
                    {usageStats.plan === 'beta' ? 'Analyses offertes' : 'Analyses gratuites'}
                  </span>
                  <span className="font-medium text-slate-900 dark:text-white shader:text-white">
                    {usageStats.freeAnalysesUsed} / {usageStats.freeAnalysesLimit}
                  </span>
                </div>
                <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      usageStats.freeAnalysesUsed >= usageStats.freeAnalysesLimit
                        ? 'bg-red-500'
                        : usageStats.plan === 'beta'
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                          : 'bg-gradient-to-r from-violet-500 to-indigo-500'
                    }`}
                    style={{ width: `${Math.min(100, (usageStats.freeAnalysesUsed / usageStats.freeAnalysesLimit) * 100)}%` }}
                  />
                </div>
                {usageStats.freeAnalysesUsed >= usageStats.freeAnalysesLimit && (
                  <p className="text-xs text-orange-600 dark:text-orange-400">
                    {usageStats.plan === 'beta'
                      ? 'Vous avez utilisé toutes vos analyses offertes. Passez au Pro pour continuer.'
                      : 'Quota atteint. Passez au Pro pour des analyses illimitées.'}
                  </p>
                )}
              </div>
            )}

            {usageStats.creditBalance > 0 && (
              <div className="pt-2 border-t border-slate-200 dark:border-slate-700 shader:border-white/10">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400 shader:text-white/85">Crédits à l'unité</span>
                  <span className="font-medium text-slate-900 dark:text-white shader:text-white">{usageStats.creditBalance}</span>
                </div>
              </div>
            )}
          </div>

          {/* By type breakdown */}
          {Object.keys(usageStats.byType).length > 0 && (
            <div className="bg-slate-50 dark:bg-slate-800/50 shader:bg-white/5 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white shader:text-white">Par type d'analyse</h3>
              <div className="space-y-2">
                {Object.entries(usageStats.byType)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => {
                    const option = ANALYSIS_OPTIONS.find(o => o.key === type);
                    return (
                      <div key={type} className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400 shader:text-white/85">
                          {option?.icon} {option?.label ?? type}
                        </span>
                        <span className="font-medium text-slate-900 dark:text-white shader:text-white">{count}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="bg-slate-50 dark:bg-slate-800/50 shader:bg-white/5 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-400 shader:text-white/85">Total (depuis l'inscription)</span>
              <span className="font-semibold text-slate-900 dark:text-white shader:text-white">{usageStats.totalAnalyses} analyse{usageStats.totalAnalyses !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="text-sm text-slate-500 py-8 text-center">
          Impossible de charger les statistiques.
        </div>
      )}
    </div>
  );
}
